import { RoleTypes } from "../../../types";
import { useAuth } from "../../store";
import apiEndpoints from "../endpoints";
import apiClient from "../exportClient";

/**
 * IMPORTANT: Backend Integration Notes
 *
 * Some naming conventions (like 'AttandanceCoordinator', 'cleateClerk')
 * appear to be typos but are actually the exact values returned by the backend API.
 * DO NOT change these values as they would break authentication and role-based functionality.
 *
 * These names must match backend responses exactly:
 * - 'AttandanceCoordinator' (user_role from login API)
 * - apiEndpoints.users.cleateClerk (backend endpoint)
 * - apiEndpoints.users.getCoordindatorProfile (backend endpoint)
 */

interface ChangePasswordData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

interface CreateUserData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  [key: string]: any; // Allow additional fields from backend
}

const usersApis = () => {
  const user_id = useAuth.getState().user.user_id;
  const userRole = useAuth.getState().user.role;

  return {
    changePassword: {
      queryKey: ["change-password"],
      queryFn: (values: ChangePasswordData) => {
        return apiClient.post(apiEndpoints.auth.changePassword, values);
      },
    },

    /**
     * Get unread notifications based on user role
     * Note: 'AttandanceCoordinator' matches backend response (keep as-is)
     */
    getUnreadNotifications: {
      queryKey: ["get-UnreadNotifications", user_id],
      queryFn: () => {
        // Build role-based query parameter (names must match backend exactly)
        const rolebaseuserid =
          userRole === "Parent"
            ? "?parent=" + user_id
            : userRole === "Teacher"
              ? "?teacher=" + user_id
              : userRole === "Student"
                ? "?student=" + user_id
                : userRole === "AttandanceCoordinator"
                  ? "?attendace_coordinator=" + user_id
                  : userRole === "School_Admin"
                    ? "?school=" + user_id
                    : "none";
        if (rolebaseuserid === "none") return Promise.resolve(null);
        return apiClient.get(
          apiEndpoints.users.getUnreadNotifications + rolebaseuserid,
        );
      },
    },

    //Get full profile details
    getCoordinatorProfile: {
      queryKey: ["get-CoordindatorProfile", user_id],
      queryFn: () => {
        return apiClient.get(apiEndpoints.users.getCoordindatorProfile);
      },
    },

    //Others
    getStudentsByParent: {
      queryKey: ["get-students", user_id],
      queryFn: () => {
        return apiClient.get(apiEndpoints.users.getStudentsByParent + user_id);
      },
    },
    getLEAAdmins: {
      queryKey: ["get-Lea-Admins"],
      queryFn: () => {
        return apiClient.get(apiEndpoints.users.getLEAAdmins);
      },
    },
    getLEASubAdmins: {
      queryKey: ["get-Lea-sub-Admins", user_id],
      queryFn: () => {
        return apiClient.get(
          apiEndpoints.users.getLEASubAdmins + user_id + "/",
        );
      },
    },
    readnotification: {
      mk: ["readnotification", user_id],
      mf: ({ id }: { id: number }) =>
        apiClient.post(apiEndpoints.users.notificationsread + id),
    },
    /**
     * Create LEA Sub Admin
     * Note: Function name has typo but kept for consistency with existing codebase
     */
    createLEASubAmdin: {
      mk: ["create-sub-lea-admin", user_id],
      mf: (values: CreateUserData) => {
        return apiClient.post(apiEndpoints.users.createLeaSubAdmin, values);
      },
    },

    getTeachersBygrade: {
      queryKey: ["get-Teachers-Bygrade"],
      queryFn: ({ id }: any) => {
        return apiClient.get(apiEndpoints.users.getTeachersBygrade + id + "/");
      },
    },
    getRoaster: {
      queryKey: ["get-roasterData", user_id],
      queryFn: (
        id: any,
        userType: RoleTypes,
        viewType?: "info" | undefined,
        schoolId?: number | null,
        leaId?: number,
        page?: number,
      ) => {
        console.log("_", id);
        const pageParam = page ? `&page=${page}` : "";
        if (viewType === "info") {
          return apiClient.get(
            userType === "Parent"
              ? apiEndpoints.users.getRoaster +
                  leaId +
                  "/" +
                  schoolId +
                  "/?user=" +
                  "parent" +
                  pageParam
              : userType === "Teacher"
                ? apiEndpoints.users.getRoaster +
                  leaId +
                  "/" +
                  schoolId +
                  "/?user=" +
                  "teacher" +
                  pageParam
                : userType === "Student"
                  ? apiEndpoints.users.getRoaster +
                    leaId +
                    "/" +
                    schoolId +
                    "/?user=" +
                    "student" +
                    pageParam
                  : userType === "AttandanceCoordinator"
                    ? apiEndpoints.users.getRoaster +
                      leaId +
                      "/" +
                      schoolId +
                      "/?user=" +
                      "attandancecoordinator" +
                      pageParam
                    : userType === "Clerk"
                      ? apiEndpoints.users.getRoaster +
                        leaId +
                        "/" +
                        schoolId +
                        "/?user=" +
                        "clerk" +
                        pageParam
                      : "",
          );
        }
        return apiClient.get(
          userType === "Parent"
            ? apiEndpoints.users.getParents +
                schoolId +
                "/" +
                (page ? `?page=${page}` : "")
            : userType === "Teacher"
              ? apiEndpoints.users.getTeachers +
                schoolId +
                "/" +
                (page ? `?page=${page}` : "")
              : userType === "Student"
                ? apiEndpoints.users.getStudents +
                  schoolId +
                  "/" +
                  (page ? `?page=${page}` : "")
                : userType === "AttandanceCoordinator"
                  ? apiEndpoints.users.getAttendanceCoordinators +
                    schoolId +
                    "/" +
                    (page ? `?page=${page}` : "")
                  : userType === "Clerk"
                    ? apiEndpoints.users.getClerks +
                      schoolId +
                      "/" +
                      (page ? `?page=${page}` : "")
                    : "",
        );
      },
    },
    createRoaster: {
      queryKey: ["create-roaster"],
      queryFn: (values: { data: any; userType: RoleTypes }) => {
        return apiClient.post(
          values.userType === "Parent"
            ? apiEndpoints.users.createParent
            : values.userType === "Teacher"
              ? apiEndpoints.users.createTeacher
              : values.userType === "Student"
                ? apiEndpoints.users.createStudent
                : values.userType === "AttandanceCoordinator"
                  ? apiEndpoints.users.createAttendanceCoordinator
                  : values.userType === "Clerk"
                    ? apiEndpoints.users.cleateClerk
                    : "",
          values.data,
        );
      },
    },
    searchUsers: {
      queryKey: ["search-users"],
      queryFn: (query: string) => {
        return apiClient.post(apiEndpoints.users.searchUsers, { query });
      },
    },
    importSisData: {
      mk: ["import-sis-data"],
      mf: (file: File) => {
        const formData = new FormData();
        formData.append("csv_file", file);
        return apiClient.post(apiEndpoints.users.importSisData, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      },
    },
  };
};

export default usersApis;
